import { skipToken, useQuery } from '@tanstack/react-query'
import { conciergeFireService, firerouterService, tenantService } from '@chilipiper/service'
import { defaultErrorCallback } from '@chilipiper/service/src/api'
import { SlugMappingResponse } from '@chilipiper/api-type-def'
import { useLocation, useRouteMatch } from 'react-router-dom'
import { getDomain, parseQuery } from '@chilipiper/utils'
import { useEffect } from 'react'
import { RouteLocation } from '../types'

const useGetTenantIdFromSubdomain = () => {
  const domain = getDomain()
  const query = useQuery({
    queryKey: ['migration', 'get-tenant-id', domain],
    queryFn: () => {
      return tenantService.getTenantIdFromSubdomain(domain)
    },
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (query.error) {
      defaultErrorCallback(query.error as any)
    }
  }, [query.error])

  return query
}

const useFireToggle = () => {
  const domain = getDomain()
  const { data: tenantId } = useGetTenantIdFromSubdomain()
  const query = useQuery({
    queryKey: ['migration', 'use-fire', 'toggle', domain],
    queryFn: () => conciergeFireService.getFireToggle(tenantId as string),
    enabled: !!tenantId,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (query.error) {
      defaultErrorCallback(query.error as any)
    }
  }, [query.error])

  return query
}

const isBookingLink = (pathname: string, params: URLSearchParams) =>
  pathname.startsWith('/book') && !params.has('rescheduleId') && !pathname.includes('/dialer/')
const isPersonalBookingLink = (queueSlug: string) => queueSlug === 'me'
const isRouterLink = (pathname: string) => pathname.startsWith('/router')

const handleMappingRedirect = (
  slugResponse?: SlugMappingResponse,
  typeResponse?: SlugMappingResponse,
  byResponse?: SlugMappingResponse,
  additionalSearchParams?: URLSearchParams,
  isPersonalWorkspace?: boolean
) => {
  const searchParams = new URLSearchParams(additionalSearchParams)
  if (!slugResponse?.fireSlug) {
    return
  }
  const baseUrl = window.location.origin.replace('.na', '')
  switch (slugResponse.entityType) {
    case 'router':
      if (typeResponse?.entityFireId) {
        searchParams.set('meetingTypeId', typeResponse.entityFireId)
      }
      if (byResponse?.fireSlug) {
        searchParams.set('by', byResponse.fireSlug)
      }
      window.location.href = `${baseUrl}/concierge-router/link/${slugResponse.fireSlug}${
        searchParams.size > 0 ? `?${searchParams.toString()}` : ''
      }`
      break
    case 'user':
      window.location.href = `${baseUrl}/${
        isPersonalWorkspace || !typeResponse ? 'me' : 'shared'
      }/${slugResponse.fireSlug}${typeResponse ? `/${typeResponse.fireSlug}` : ''}${
        searchParams.size > 0 ? `?${searchParams.toString()}` : ''
      }`
      break
    case 'queue':
      if (typeResponse?.entityFireId) {
        searchParams.set('meetingTypeId', typeResponse.entityFireId)
      }
      window.location.href = `${baseUrl}/round-robin/${slugResponse.fireSlug}${
        searchParams.size > 0 ? `?${searchParams.toString()}` : ''
      }`
      break
    case 'ownershipSchedulingLink':
      if (typeResponse?.entityFireId) {
        searchParams.set('meetingTypeId', typeResponse.entityFireId)
      }
      window.location.href = `${baseUrl}/ownership/${slugResponse.fireSlug}${
        searchParams.size > 0 ? `?${searchParams.toString()}` : ''
      }`
      break
    case 'groupSchedulingLink':
      if (typeResponse?.entityFireId) {
        searchParams.set('meetingTypeId', typeResponse.entityFireId)
      }
      window.location.href = `${baseUrl}/group/${slugResponse.fireSlug}${
        searchParams.size > 0 ? `?${searchParams.toString()}` : ''
      }`
      break
    default:
      window.location.href = `${window.location.origin}/book/not-available`
      break
  }
  return Promise.resolve(null)
}

const useFormId = () => {
  const location = useLocation() as RouteLocation
  const routeMatch = useRouteMatch()
  const { meetingTypeSlugMapping, isLoadingMeetingTypeSlugMapping } = useMeetingType()
  const domain = getDomain()

  const { data, isPending } = useQuery({
    queryKey: [
      'migration',
      'formId',
      'mapping',
      domain,
      location.pathname,
      meetingTypeSlugMapping?.legacySlug || 'no-type',
    ],
    queryFn: () => {
      if (!location.pathname) {
        return Promise.resolve(null)
      }
      const searchParams = new URLSearchParams(window.location.search)
      searchParams.delete('type')
      searchParams.delete('domain')
      if (meetingTypeSlugMapping?.legacySlug) {
        return firerouterService
          .mapping(domain, 'meeting-type-form', meetingTypeSlugMapping.legacySlug)
          .then(({ fireSlug }) => fireSlug)
      }
      if (isRouterLink(location.pathname)) {
        const [routerName] = Object.values<string>(routeMatch.params)
        return firerouterService
          .mapping(domain, 'router-form', routerName)
          .then(({ fireSlug }) => fireSlug)
      }
      if (
        isBookingLink(location.pathname, searchParams) &&
        !isPersonalBookingLink(location.pathname)
      ) {
        const [path] = Object.values<string>(routeMatch.params)
        const [queueSlug] = path.split('/')
        return firerouterService
          .mapping(domain, 'queue-default-meeting-type-form', queueSlug)
          .then(({ fireSlug }) => fireSlug)
      }
      return Promise.resolve(undefined)
    },
    enabled: !isLoadingMeetingTypeSlugMapping,
  })
  return {
    formId: data,
    isLoadingFormId: isPending,
  }
}

const useMeetingType = () => {
  const query = parseQuery()
  const type = query.type
  const domain = getDomain()

  const { data, isPending } = useQuery({
    queryKey: ['migration', 'meeting-type', 'mapping', domain, type || 'no-type'],
    queryFn: type
      ? () => {
          return Promise.all([
            firerouterService.mapping(domain, 'adminSchedulingLink', type),
            firerouterService.mappingByLegacyId(domain, 'adminSchedulingLink', type),
          ]).then(([meetingTypeBySlugResponse, meetingTypeByIdResponse]) => {
            if (!meetingTypeBySlugResponse.entityLegacyId) {
              return meetingTypeByIdResponse
            }
            return meetingTypeBySlugResponse
          })
        }
      : skipToken,
  })
  return {
    meetingTypeSlugMapping: data,
    isLoadingMeetingTypeSlugMapping: Boolean(type) && isPending,
  }
}

export const useFireRedirect = () => {
  const location = useLocation() as RouteLocation
  const routeMatch = useRouteMatch()
  const query = parseQuery()
  const domain = getDomain()
  const type = query.type
  const { formId, isLoadingFormId } = useFormId()
  const { meetingTypeSlugMapping, isLoadingMeetingTypeSlugMapping } = useMeetingType()
  const { data: useAllProductsFire, isPending: isLoadingFireToggle } = useFireToggle()

  const { isPending: isLoadingRedirect, error } = useQuery({
    queryKey: [
      'migration',
      'redirect',
      domain,
      location.pathname,
      window.location.search,
      type || 'no-type',
      query.by || 'no-by',
      formId || '',
      meetingTypeSlugMapping?.entityLegacyId || '',
    ],
    queryFn: () => {
      if (!location.pathname) {
        return Promise.resolve(null)
      }
      const searchParams = new URLSearchParams(window.location.search)
      searchParams.delete('type')
      searchParams.delete('domain')
      let slugPromise: Promise<SlugMappingResponse | undefined> = Promise.resolve(undefined)
      if (isBookingLink(location.pathname, searchParams)) {
        const [path] = Object.values<string>(routeMatch.params)
        const [queueSlug, userSlug] = path.split('/')
        if (isPersonalBookingLink(queueSlug)) {
          slugPromise = firerouterService.mapping(domain, 'user', userSlug)
        } else {
          void firerouterService.mapping(domain, 'user', userSlug).then(({ fireSlug }) => {
            if (fireSlug) {
              searchParams.set('assignee', fireSlug)
            }
          })
          slugPromise = firerouterService
            .mapping(domain, 'queue-algorithm', queueSlug)
            .then(({ fireSlug: queueAlgorithm }) => {
              if (queueAlgorithm === 'Group') {
                return firerouterService.mapping(domain, 'groupSchedulingLink', queueSlug)
              }
              if (queueAlgorithm === 'Ownership') {
                return firerouterService.mapping(domain, 'ownershipSchedulingLink', queueSlug)
              }
              return firerouterService.mapping(domain, 'queue', queueSlug)
            })
        }
      } else if (isRouterLink(location.pathname)) {
        const [routerName] = Object.values<string>(routeMatch.params)
        slugPromise = firerouterService.mapping(domain, 'router', routerName)
      } else {
        return Promise.resolve(undefined)
      }
      const promises: Promise<SlugMappingResponse | undefined>[] = [slugPromise]
      promises.push(
        query.by ? firerouterService.mapping(domain, 'user', query.by) : Promise.resolve(undefined)
      )
      promises.push(
        meetingTypeSlugMapping?.entityLegacyId
          ? firerouterService.mappingByLegacyId(
              domain,
              'meeting-type-type',
              meetingTypeSlugMapping.entityLegacyId
            )
          : Promise.resolve(undefined)
      )
      if (formId) {
        Array.from(searchParams.entries()).forEach(([key]) => {
          promises.push(
            firerouterService.mappingByLegacyId(domain, 'form.block.mapping', `${formId}.${key}`)
          )
        })
      }
      return Promise.all(promises).then(
        ([slugResponse, byResponse, meetingTypeTypeResponse, ...blockResponses]) => {
          const isPersonalWorkspace =
            !meetingTypeTypeResponse || meetingTypeTypeResponse.fireSlug === 'Personal'
          const newSearchParams = new URLSearchParams(searchParams)
          blockResponses.forEach(block => {
            if (
              block?.entityFireId &&
              block.entityLegacyId &&
              block?.entityFireId !== block.entityLegacyId
            ) {
              const formFieldName = block.entityLegacyId.replace(`${formId}.`, '')
              newSearchParams.set(block.entityFireId, searchParams.get(formFieldName) || '')
              if (!formFieldName.includes('utm_')) {
                newSearchParams.delete(formFieldName)
              }
            }
          })
          return handleMappingRedirect(
            slugResponse,
            meetingTypeSlugMapping,
            byResponse,
            newSearchParams,
            isPersonalWorkspace
          )
        }
      )
    },
    enabled: Boolean(useAllProductsFire) && !isLoadingFormId && !isLoadingMeetingTypeSlugMapping,
  })

  useEffect(() => {
    if (error) {
      defaultErrorCallback(error as any)
      window.location.href = `${window.location.origin}/book/not-available`
    }
  }, [error])

  return {
    data: useAllProductsFire,
    isLoading: isLoadingFireToggle || (Boolean(useAllProductsFire) && isLoadingRedirect),
  }
}
